import { useEffect, useRef, useState, type ReactNode } from "react";

/** Stile icona condiviso da tutte le voci del Rail / BottomNav. */
export const IC_CLASS =
  "w-5 h-5 stroke-current stroke-[1.6px] fill-none [stroke-linecap:round] [stroke-linejoin:round]";

/** Messaggio di feedback + tono con cui colorare il toast. */
export type RailToast = { tone: "info" | "error"; text: string };

/**
 * Messaggio effimero: lo mostra e lo spegne dopo `ms`. Il timer viene
 * cancellato all'unmount e a ogni nuovo flash, cosi' un secondo messaggio non
 * eredita la scadenza del primo.
 */
export function useFlash<T>(ms: number): [T | null, (next: T) => void] {
  const [value, setValue] = useState<T | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  function flash(next: T) {
    setValue(next);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setValue(null), ms);
  }

  return [value, flash];
}

interface RailButtonProps {
  /** Etichetta accessibile: descrive l'azione, non solo la voce visibile. */
  ariaLabel: string;
  /** Testo sotto l'icona (il chiamante ci mette anche lo stato "Esco…"). */
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Azione in corso: disabilita il pulsante e lo sbiadisce. */
  busy?: boolean;
  toast?: RailToast | null;
  /** Solo icona, 36px: tema ed Esci in fondo al rail stanno in una riga sola,
   *  cosi' su un portatile il menu intero entra senza scorrere. Il nome resta
   *  nel tooltip e per lo screen reader. */
  compatto?: boolean;
}

/**
 * Pulsante di servizio in fondo al Rail (Aggiorna / Esci) con il suo toast di
 * feedback: stessa geometria e stessi stati delle voci di navigazione, ma
 * l'azione non e' un cambio di rotta.
 */
export function RailButton({
  ariaLabel,
  label,
  icon,
  onClick,
  busy = false,
  toast,
  compatto = false,
}: RailButtonProps) {
  return (
    <>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        disabled={busy}
        title={compatto ? ariaLabel : undefined}
        className={
          compatto
            ? "relative grid h-9 w-9 place-items-center rounded-md text-white/75 hover:bg-white/[0.06] hover:text-white transition-colors duration-[120ms] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            : "relative w-[68px] px-1 py-1.5 rounded-md flex flex-col items-center gap-1 text-center text-[10.5px] font-medium leading-[1.15] text-white/75 hover:bg-white/[0.06] hover:text-white transition-colors duration-[120ms] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        }
      >
        {icon}
        {!compatto && label}
      </button>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={[
            "fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-sm rounded-md border px-3.5 py-2",
            "text-[12.5px] shadow-popover",
            toast.tone === "error"
              ? "border-red/20 bg-red-soft text-ink"
              : "border-blue/20 bg-blue-soft text-ink",
          ].join(" ")}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}
